import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { DesktopPolicyAnswer, DesktopPolicyBody, DesktopPolicyState } from './desktop-types';

/** Reads and saves a machine's cards, schedule and pause (`/v1/admin/desktop-gpus/{machine}/policy`). */
export interface PolicyApi {
  read: (machine: string) => Promise<DesktopPolicyState>;
  save: (machine: string, body: DesktopPolicyBody) => Promise<DesktopPolicyAnswer>;
}

/**
 * Where a save is: `saving`; `confirming` (sent, reading back to see the machine hold it); `applied`; `sent` (sent, the
 * machine has not shown it yet); `waiting` (the machine is away: it applies when it connects); `too-old`; `changed`
 * (the policy changed since the editor opened it); `failed`.
 */
export type SavePhase =
  | 'idle'
  | 'saving'
  | 'confirming'
  | 'applied'
  | 'sent'
  | 'waiting'
  | 'too-old'
  | 'changed'
  | 'failed';

/** Read-backs after a save, in ms from it: a few, not a poll (the screen's own read stays at five seconds). */
export const CONFIRM_AT = [2500, 5000, 10000];

const statusOf = (e: unknown) =>
  typeof e === 'object' && e !== null && 'status' in e
    ? Number((e as { status: unknown }).status)
    : 0;

/** A save that never reached Paige: the browser's own words ("Failed to fetch") say nothing to a person. */
export const UNREACHABLE = 'Could not reach Paige. Check the connection and save again.';

/**
 * A refused save: a worker too old, the policy changed meanwhile (both 409), or anything else; and why, in words. A
 * failure with no HTTP status is the network's, said as such.
 */
function failureOf(e: unknown): { phase: SavePhase; words: string } {
  const status = statusOf(e);
  const words = status === 0 ? UNREACHABLE : e instanceof Error ? e.message : 'Could not save.';
  if (status !== 409) return { phase: 'failed', words };
  return { phase: /update the worker/i.test(words) ? 'too-old' : 'changed', words };
}

const holds = (s: DesktopPolicyState, changedAt: string) =>
  s.source === 'live' && !s.pending && s.policy?.changedAt === changedAt;

/** Reads back a few times after a save until the machine shows the change: `applied`, else `sent` after the last. */
function useConfirm(
  machine: string,
  api: PolicyApi,
  setState: Dispatch<SetStateAction<DesktopPolicyState | null>>,
  setPhase: Dispatch<SetStateAction<SavePhase>>,
) {
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const stop = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  const start = (changedAt: string) =>
    CONFIRM_AT.forEach((ms, i) => {
      const last = i === CONFIRM_AT.length - 1;
      const settle = (s: DesktopPolicyState | null) =>
        setPhase((p) =>
          p !== 'confirming' ? p : s && holds(s, changedAt) ? 'applied' : last ? 'sent' : p,
        );
      const read = () =>
        void api.read(machine).then(
          (s) => {
            setState(s);
            settle(s);
          },
          () => settle(null),
        );
      timers.current.push(setTimeout(read, ms));
    });
  return { start, stop };
}

/** A save and its confirmation: the phase, why it failed, and the state read back. */
export function usePolicySave(machine: string, api: PolicyApi) {
  const [phase, setPhase] = useState<SavePhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<DesktopPolicyState | null>(null);
  const confirm = useConfirm(machine, api, setState, setPhase);

  /** Sends the body; answers the phase it came to and the policy it made. */
  const save = async (
    body: DesktopPolicyBody,
  ): Promise<{ phase: SavePhase; changedAt: string | null }> => {
    confirm.stop();
    setPhase('saving');
    setError(null);
    try {
      const answer = await api.save(machine, body);
      const changedAt = answer.state.policy?.changedAt ?? null;
      setState(answer.state);
      const next = answer.outcome === 'waiting' ? 'waiting' : 'confirming';
      setPhase(next);
      if (next === 'confirming' && changedAt) confirm.start(changedAt);
      return { phase: next, changedAt };
    } catch (e) {
      const failed = failureOf(e);
      setError(failed.words);
      setPhase(failed.phase);
      return { phase: failed.phase, changedAt: null };
    }
  };

  return { phase, error, state, save };
}
